// FAFI Touch T1 (FAFI-Touch-Plan §1/§7) — the KeyboardInput sibling. Held pseudo-codes + the stick →
// the same PlayerInput shape, through the same `mapper.map(...)` call site `main.ts` already uses. Pure
// (no DOM, no globals): the DOM shell (`ui/touchLayer.ts`) feeds pointer data in; `main.ts` reads
// samples out. Drag accumulators are take-and-reset, one consumer per frame — the same contract as
// `CameraControl.takeFrameDx`, so the goal-aim and camera plumbing stay drop-in.

import type { PlayerInput } from '@fafi/shared';
import { gamepadMove } from '../aimModel';
import { TouchStick, type StickConfig } from './touchStick';
import { TOUCH_CODE } from './touchButtons';

/** Stick deadzone (fraction of radius) — mirrors the §13.2 gamepad deadzone role. */
export const TOUCH_DEADZONE = 0.12;

/**
 * D159 — the facing-aim ladder, decoupled from the camera. The stick is pure run direction (the
 * designer's "same as WASD"); AIM follows it while steering, and when the thumb releases: the
 * opponent goal while attacking (snap shots and forward passes default there — the sim-side
 * soft-lock and CALL-bending pick the receiver), the last run direction while defending (a
 * clearance goes where you were heading), then the ball, then the goal. The goal-aim drag still
 * overrides all of this while SHOOT is held.
 */
export function pickTouchAim(
  stickWorld: { x: number; y: number } | null,
  lastMove: { x: number; y: number } | null,
  attacking: boolean,
  goalYaw: number,
  ballYaw: number | null,
): number {
  if (stickWorld !== null) return Math.atan2(stickWorld.y, stickWorld.x);
  if (attacking) return goalYaw;
  if (lastMove !== null) return Math.atan2(lastMove.y, lastMove.x);
  return ballYaw ?? goalYaw;
}

const KICK_CODES: readonly string[] = [TOUCH_CODE.A, TOUCH_CODE.B, TOUCH_CODE.LONG];

export class TouchInput {
  readonly stick: TouchStick;
  private readonly held = new Set<string>();
  private aimDx = 0;
  private aimDy = 0;
  private camDx = 0;
  private recenter = false;
  private lastMove: { x: number; y: number } | null = null;
  private stickLive = false;

  constructor(stickCfg?: StickConfig) {
    this.stick = new TouchStick(stickCfg);
  }

  press(code: string): void {
    if (code !== '') this.held.add(code);
  }

  release(code: string): void {
    this.held.delete(code);
  }

  /** T-TCH-006 — the no-stuck-buttons guarantee (pointercancel, blur, hidden tab): ALL neutral. */
  clearAll(): void {
    this.held.clear();
    this.stick.up();
    this.aimDx = 0;
    this.aimDy = 0;
    this.camDx = 0;
    this.recenter = false;
    this.stickLive = false;
  }

  isHeld(code: string): boolean {
    return this.held.has(code);
  }

  /** The ceremony nudge (net path) — is any kick verb down right now? */
  anyKickHeld(): boolean {
    return KICK_CODES.some((c) => this.held.has(c));
  }

  /** Held pseudo-codes for the ContextMapper — plus Touch:sprint synthesized off the stick ring. */
  heldCodes(): ReadonlySet<string> {
    if (!this.stick.sample().sprint) return this.held;
    const out = new Set(this.held);
    out.add(TOUCH_CODE.SPRINT);
    return out;
  }

  aimDrag(dx: number, dy: number): void {
    this.aimDx += dx;
    this.aimDy += dy;
  }
  takeAimDx(): number {
    const v = this.aimDx;
    this.aimDx = 0;
    return v;
  }
  takeAimDy(): number {
    const v = this.aimDy;
    this.aimDy = 0;
    return v;
  }

  camDrag(dx: number): void {
    this.camDx += dx;
  }
  takeCamDx(): number {
    const v = this.camDx;
    this.camDx = 0;
    return v;
  }

  noteRecenter(): void {
    this.recenter = true;
  }
  takeRecenter(): boolean {
    const v = this.recenter;
    this.recenter = false;
    return v;
  }

  /** Latest non-zero world-space move direction (unit-ish) — the camera rests toward it. */
  lastMoveWorld(): { x: number; y: number } | null {
    return this.lastMove;
  }

  /** Is the thumb on the stick right now (as of the last sample)? Gates the camera's run-follow. */
  stickActive(): boolean {
    return this.stickLive;
  }

  /**
   * The per-tick sample — same shape and conventions as `KeyboardInput.sample` (camera-relative move,
   * caller-authored aim, mapper-authored buttons). `gamepadMove` is the §13.2 stick math, unchanged.
   */
  sample(cameraYaw: number, aimYaw: number, aimElev: number, buttons: number): PlayerInput {
    const s = this.stick.sample();
    const mv = gamepadMove(s.x, s.y, cameraYaw, TOUCH_DEADZONE);
    this.stickLive = s.active;
    if (mv.x !== 0 || mv.y !== 0) this.lastMove = { x: mv.x, y: mv.y };
    return { moveX: mv.x, moveY: mv.y, aimYaw, aimElev, buttons, emote: 0 };
  }
}
